import { ref } from 'vue'
import { logger } from '@/utils/logger'
import type {
  IAnalyticsCallback,
  IAnalyticsEvent,
  IAnalyticsService,
} from '@/types/analytics-types'

interface IAnalyticsState {
  analyticsCallback: IAnalyticsCallback | null
  userId: string | undefined
  sessionId: string | undefined
  chatbotId: string | undefined
  tenantId: string | undefined
  source: string
}

const state = ref<IAnalyticsState>({
  analyticsCallback: null,
  userId: undefined,
  sessionId: undefined,
  chatbotId: undefined,
  tenantId: undefined,
  source: 'chatbot',
})

export interface IAnalyticsComposable extends IAnalyticsService {
  setAnalyticsCallback: (_callback: IAnalyticsCallback | null) => void
  setSource: (_source: string) => void
}

export function useAnalytics(): IAnalyticsComposable {
  const setAnalyticsCallback = (callback: IAnalyticsCallback | null): void => {
    state.value.analyticsCallback = callback

    if (callback) {
      logger.info('Analytics callback configured', undefined, {
        component: 'useAnalytics',
      })
    } else {
      logger.info('Analytics callback removed', undefined, {
        component: 'useAnalytics',
      })
    }
  }

  const setUserContext = (userId?: string, sessionId?: string): void => {
    state.value.userId = userId
    state.value.sessionId = sessionId

    logger.debug(
      'Analytics user context updated',
      { userId, sessionId },
      { component: 'useAnalytics' }
    )
  }

  const setChatbotContext = (chatbotId?: string, tenantId?: string): void => {
    state.value.chatbotId = chatbotId
    state.value.tenantId = tenantId

    logger.debug(
      'Analytics chatbot context updated',
      { chatbotId, tenantId },
      { component: 'useAnalytics' }
    )
  }

  const setSource = (source: string): void => {
    state.value.source = source
    logger.debug(
      'Analytics source updated',
      { source },
      { component: 'useAnalytics' }
    )
  }

  const isEnabled = (): boolean => {
    return state.value.analyticsCallback !== null
  }

  const trackEvent = (event: IAnalyticsEvent): void => {
    if (!isEnabled()) {
      logger.debug(
        'Analytics not enabled, skipping event',
        { event },
        { component: 'useAnalytics' }
      )
      return
    }

    try {
      const callback = state.value.analyticsCallback!

      const result = callback(event)
      if (result && typeof result.then === 'function') {
        result.catch((error: Error) => {
          logger.warn(
            'Analytics callback promise rejected (non-blocking)',
            { eventName: event.eventName, error: error.message },
            { component: 'useAnalytics' },
            error
          )
        })
      }

      logger.debug(
        'Analytics event tracked',
        { eventName: event.eventName, payload: event.payload },
        { component: 'useAnalytics' }
      )
    } catch (error) {
      logger.warn(
        'Analytics callback error (non-blocking)',
        {
          eventName: event.eventName,
          error: error instanceof Error ? error.message : String(error),
        },
        { component: 'useAnalytics' },
        error instanceof Error ? error : undefined
      )
    }
  }

  return {
    trackEvent,
    setUserContext,
    setChatbotContext,
    isEnabled,
    setAnalyticsCallback,
    setSource,
  }
}

export default useAnalytics
